import {Exclude, instanceToPlain} from 'class-transformer';
import {IsDefined} from 'class-validator';

import {captureException} from '@sentry/angular';

import {
	AbstractTriggerEntity,
	BaseCycleStatusTriggerEntity,
	ClientEventTriggerEntity,
	NotificationTimeoutTriggerEntity,
	StationStatusTriggerEntity,
	TriggerActionEntity,
	TriggerEntity,
	TriggerEventType,
} from '../trigger';

export class BaseTriggerEntity extends AbstractTriggerEntity {
	static override _uniqueId = '';

	@Exclude()
	public override readonly validity: ClassValidatorSignals = classValidatorSignals.bind(this)();

	@IsDefined({message: 'trigger.errors.invalidTriggerAction'})
	action: TriggerActionEntity;

	constructor(init?: Partial<BaseTriggerEntity>) {
		super(undefined, init);
		Object.assign(this, init);
		if (init?.action) this.action = TriggerActionEntity.create(init?.action?.type, init?.action);
	}

	toJSON(): AbstractTriggerEntity {
		return instanceToPlain(this, {}) as AbstractTriggerEntity;
	}
}

export function createTriggerFromData(rawTrigger?: AbstractTriggerEntity): TriggerEntity {
	if (!rawTrigger) return new BaseTriggerEntity();
	const factory = (trigger: AbstractTriggerEntity) => {
		try {
			switch (trigger.event) {
				case TriggerEventType.CycleStatus:
					if (BaseCycleStatusTriggerEntity.is(trigger)) {
						return BaseCycleStatusTriggerEntity.create(trigger);
					}
					break;
				case TriggerEventType.NotificationTimeout:
					return new NotificationTimeoutTriggerEntity(trigger as NotificationTimeoutTriggerEntity);
				case TriggerEventType.StationStatus:
					if (StationStatusTriggerEntity.is(trigger)) {
						return StationStatusTriggerEntity.create(trigger);
					}
					break;
				case TriggerEventType.ClientEvent:
					if (ClientEventTriggerEntity.is(trigger)) {
						return ClientEventTriggerEntity.create(trigger);
					}
					break;
				default:
					return new BaseTriggerEntity(trigger);
			}
			return new BaseTriggerEntity(trigger);
		} catch (e) {
			tryCatchFn(() => {
				captureException(new Error(e), {extra: {value: trigger, url: window.location.href}});
			});
			return null;
		}
	};
	if (!factory) throw new Error(`Unsupported event type: ${rawTrigger.event}`);
	return factory(rawTrigger);
}
